import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { App, Spin, Image, Space, Typography, Result } from 'antd';
import PortalLayout from './PortalLayout';
import PortalErrorPage from './PortalErrorPage';
import PortalLineItemApproval, { ApprovalAction } from '../../components/Portal/PortalLineItemApproval';
import PortalSignaturePad, { PortalSignaturePadHandle } from '../../components/Portal/PortalSignaturePad';
import { portalApi, PortalEstimateView } from '../../services/api/portalApi';
import { PortalDVISection } from '../../components/DVI';

const { Text } = Typography;

const PortalEstimatePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { message } = App.useApp();
  const [data, setData] = useState<PortalEstimateView | null>(null);
  const [error, setError] = useState<'not_found' | 'expired' | 'rate_limited' | 'unknown' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [doneAction, setDoneAction] = useState<ApprovalAction | null>(null);
  const sigRef = useRef<PortalSignaturePadHandle>(null);

  useEffect(() => {
    if (!token) return;
    portalApi.getEstimate(token)
      .then(setData)
      .catch((err) => {
        const status = err?.response?.status;
        setError(status === 404 ? 'not_found' : status === 429 ? 'rate_limited' : 'unknown');
      });
  }, [token]);

  if (error) {
    return <PortalErrorPage variant={error} />;
  }
  if (!data) {
    return (
      <PortalLayout title="Loading…" shopName="CNF Auto">
        <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
      </PortalLayout>
    );
  }

  if (doneAction) {
    return (
      <PortalLayout title="" shopName={data.shop_name}>
        <Result
          status="success"
          title="Thanks!"
          subTitle={
            doneAction === 'decline_all'
              ? 'We received your response. The shop will follow up.'
              : 'Your approval has been received. The shop will start the work.'
          }
        />
      </PortalLayout>
    );
  }

  const handleApprove = async ({ action, line_item_ids }: { action: ApprovalAction; line_item_ids?: number[] }) => {
    if (!token) return;
    if (!sigRef.current || sigRef.current.isEmpty()) {
      message.warning('Please sign before approving');
      return;
    }
    const signature_data_url = sigRef.current.toDataURL();
    setSubmitting(true);
    try {
      await portalApi.approveEstimate(token, { action, line_item_ids, signature_data_url });
      setDoneAction(action);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) setError('not_found');
      else message.error(err?.response?.data?.error || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Sum the estimate's fee snapshot rows into a taxable / non-taxable split so the
  // portal signing total is fee-inclusive and matches the bill. Inlined (rather than
  // importing the helper) so a component-level mock of PortalLineItemApproval that
  // only stubs the default export doesn't break this page.
  const portalFees = (data.fees || []).reduce(
    (acc: { taxable: number; nonTaxable: number }, f: any) => {
      const amount = Number(f?.computed_amount ?? (f?.calculation_method === 'flat' ? f?.value : 0)) || 0;
      if (f?.is_taxable) acc.taxable += amount;
      else acc.nonTaxable += amount;
      return acc;
    },
    { taxable: 0, nonTaxable: 0 }
  );

  const customer = data.customer;
  const vehicle = data.vehicle;
  const vehicleStr = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');
  const customerStr = [customer.first_name, customer.last_name].filter(Boolean).join(' ');

  return (
    <PortalLayout
      title="Estimate for review"
      shopName={data.shop_name}
      subtitle={`${customerStr} · ${vehicleStr}${data.estimate.valid_until ? ` · Valid until ${data.estimate.valid_until}` : ''}`}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {data.photos.length > 0 && (
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
            {data.photos.map((p) => (
              <Image
                key={p.id}
                src={p.file_path}
                alt={p.sub_type}
                width={96}
                height={96}
                style={{ objectFit: 'cover', borderRadius: 4 }}
              />
            ))}
          </div>
        )}

        {data.dvi && <PortalDVISection dvi={data.dvi} />}

        <PortalLineItemApproval
          lineItems={data.line_items}
          taxRate={Number(data.estimate?.tax_rate) || 0}
          wholeJobDiscount={
            (data.estimate as any)?.discount_type && (data.estimate as any)?.discount_value != null
              ? { type: (data.estimate as any).discount_type, value: Number((data.estimate as any).discount_value) }
              : undefined
          }
          fees={portalFees}
          onApprove={handleApprove}
          disabled={submitting}
        />

        <PortalSignaturePad ref={sigRef} onCapture={() => {}} />
      </Space>
    </PortalLayout>
  );
};

export default PortalEstimatePage;
